package com.merkost.handpass.admin.api

import com.merkost.handpass.api.dto.admin.*
import com.merkost.handpass.api.dto.report.ReportResponse
import com.merkost.handpass.api.dto.report.UserReportResponse
import com.merkost.handpass.api.dto.listing.CategoryResponse
import com.merkost.handpass.api.dto.listing.ListingResponse
import com.merkost.handpass.api.enums.ImageStatus
import com.merkost.handpass.api.dto.user.AdminRoleResponse
import com.merkost.handpass.api.dto.storage.UploadUrlRequest
import com.merkost.handpass.api.dto.storage.UploadUrlResponse
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class ApiResponse<T>(
    val data: T? = null,
    val error: String? = null
)

@Serializable
data class PaginatedResponse<T>(
    val data: List<T> = emptyList(),
    val meta: PaginationMeta? = null,
    val error: String? = null
)

suspend fun fetchAdminRole(): AdminRoleResponse? {
    val client = AdminApiClient.httpClient
    val response = client.get("/api/v1/users/me/admin-role")
    if (!response.status.isSuccess()) return null
    return response.body<ApiResponse<AdminRoleResponse>>().data
}

interface AdminRepositoryApi {
    suspend fun getStats(): AdminStatsResponse?
    suspend fun getFeatureFlags(): List<FeatureFlagDto>?
    suspend fun getCategoryTree(): List<CategoryResponse>?
    suspend fun createPartnerListing(request: AdminCreateListingRequest): Result<ListingResponse>
    suspend fun getSellerUploadUrl(sellerId: String, contentType: String, fileSize: Long): UploadUrlResponse?
    suspend fun setFeatureFlag(key: String, enabled: Boolean): Result<Unit>
    suspend fun getUsers(page: Int = 1, pageSize: Int = 20, search: String? = null, status: String? = null, sortBy: String? = null, sortAsc: Boolean = false): PaginatedResponse<AdminUserListItem>
    suspend fun getUserDetail(userId: String): AdminUserDetail?
    suspend fun getListings(page: Int = 1, pageSize: Int = 20, search: String? = null, status: String? = null, sellerId: String? = null, sortBy: String? = null, sortAsc: Boolean = false): PaginatedResponse<AdminListingListItem>
    suspend fun getListingDetail(listingId: String): AdminListingDetail?
    suspend fun getBanPreview(userId: String): BanPreviewResponse?
    suspend fun getPendingReports(page: Int = 1, pageSize: Int = 20): PaginatedResponse<ReportResponse>
    suspend fun getPendingUserReports(page: Int = 1, pageSize: Int = 20): PaginatedResponse<UserReportResponse>
    suspend fun getImageQueue(page: Int = 1, pageSize: Int = 50, status: ImageStatus? = null): PaginatedResponse<AdminImageListItem>
    suspend fun getModerationLogs(page: Int = 1, pageSize: Int = 20, adminId: String? = null, action: String? = null, sortBy: String? = null, sortAsc: Boolean = false, dateFrom: String? = null, dateTo: String? = null): PaginatedResponse<ModerationLogEntry>
    suspend fun dismissReport(reportId: String): Result<Unit>
    suspend fun actionReport(reportId: String): Result<Unit>
    suspend fun dismissUserReport(reportId: String): Result<Unit>
    suspend fun actionUserReport(reportId: String): Result<Unit>
    suspend fun approveImage(id: String): Result<Unit>
    suspend fun rejectImage(id: String): Result<Unit>
    suspend fun suspendListing(listingId: String, reason: String? = null): Result<Unit>
    suspend fun restoreListing(listingId: String, reason: String? = null): Result<Unit>
    suspend fun warnUser(userId: String, reason: String? = null): Result<Unit>
    suspend fun suspendUser(userId: String, durationDays: Int, reason: String? = null): Result<Unit>
    suspend fun banUser(userId: String, reason: String? = null): Result<Unit>
    suspend fun restoreUser(userId: String, reason: String? = null): Result<Unit>
    suspend fun getQueue(page: Int = 1, pageSize: Int = 20, source: String? = null, sort: String? = null, reason: String? = null, view: String? = null): AdminQueueResponse?
    suspend fun bulkAction(request: BulkActionRequest): Result<BulkActionResponse>
    suspend fun getSavedViews(): SavedViewsResponse?
    suspend fun putSavedViews(views: List<SavedView>): Result<Unit>
    suspend fun listNotes(targetType: String, targetId: String): AdminNotesResponse?
    suspend fun createNote(targetType: String, targetId: String, body: String): Result<AdminNote>
    suspend fun deleteNote(noteId: String): Result<Unit>
    suspend fun listAuditEntries(targetType: String, targetId: String): AuditEntriesResponse?
    suspend fun recordView(targetType: String, targetId: String): Result<Unit>
    suspend fun getUserConversations(userId: String): AdminConversationsResponse?
    suspend fun getListingConversations(listingId: String): AdminConversationsResponse?
    suspend fun getConversationMessages(conversationId: String): AdminMessagesResponse?
}

class AdminRepositoryImpl : AdminRepositoryApi {
    private val client get() = AdminApiClient.httpClient

    override suspend fun getStats(): AdminStatsResponse? {
        val response = client.get("/api/v1/admin/stats")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminStatsResponse>>().data
    }

    override suspend fun getFeatureFlags(): List<FeatureFlagDto>? {
        val response = client.get("/api/v1/admin/feature-flags")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<List<FeatureFlagDto>>>().data
    }

    override suspend fun getCategoryTree(): List<CategoryResponse>? {
        val response = client.get("/api/v1/categories")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<List<CategoryResponse>>>().data
    }

    override suspend fun createPartnerListing(request: AdminCreateListingRequest): Result<ListingResponse> = runCatching {
        val response = client.post("/api/v1/admin/listings") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) error("Failed to create listing (${response.status.value})")
        response.body<ApiResponse<ListingResponse>>().data ?: error("Empty response")
    }

    override suspend fun getSellerUploadUrl(sellerId: String, contentType: String, fileSize: Long): UploadUrlResponse? {
        val response = client.post("/api/v1/admin/users/$sellerId/storage/upload-url") {
            this.contentType(ContentType.Application.Json)
            setBody(UploadUrlRequest(bucket = "listing-images", contentType = contentType, fileSize = fileSize))
        }
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<UploadUrlResponse>>().data
    }

    override suspend fun setFeatureFlag(key: String, enabled: Boolean): Result<Unit> = runResult {
        val response = client.put("/api/v1/admin/feature-flags/$key") {
            contentType(ContentType.Application.Json)
            setBody(UpdateFeatureFlagRequest(enabled))
        }
        if (!response.status.isSuccess()) error("Failed to update flag (${response.status.value})")
    }

    override suspend fun getUsers(page: Int, pageSize: Int, search: String?, status: String?, sortBy: String?, sortAsc: Boolean): PaginatedResponse<AdminUserListItem> {
        val response = client.get("/api/v1/admin/users") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            search?.let { parameter("search", it) }
            status?.let { parameter("status", it) }
            sortBy?.let { parameter("sortBy", it) }
            if (sortBy != null) parameter("sortAsc", sortAsc)
        }
        return response.body()
    }

    override suspend fun getUserDetail(userId: String): AdminUserDetail? {
        val response = client.get("/api/v1/admin/users/$userId")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminUserDetail>>().data
    }

    override suspend fun getListings(page: Int, pageSize: Int, search: String?, status: String?, sellerId: String?, sortBy: String?, sortAsc: Boolean): PaginatedResponse<AdminListingListItem> {
        val response = client.get("/api/v1/admin/listings") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            search?.let { parameter("search", it) }
            status?.let { parameter("status", it) }
            sellerId?.let { parameter("sellerId", it) }
            sortBy?.let { parameter("sortBy", it) }
            if (sortBy != null) parameter("sortAsc", sortAsc)
        }
        return response.body()
    }

    override suspend fun getListingDetail(listingId: String): AdminListingDetail? {
        val response = client.get("/api/v1/admin/listings/$listingId")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminListingDetail>>().data
    }

    override suspend fun getBanPreview(userId: String): BanPreviewResponse? {
        val response = client.get("/api/v1/admin/users/$userId/ban-preview")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<BanPreviewResponse>>().data
    }

    override suspend fun getPendingReports(page: Int, pageSize: Int): PaginatedResponse<ReportResponse> {
        val response = client.get("/api/v1/admin/reports") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }
        return response.body()
    }

    override suspend fun getPendingUserReports(page: Int, pageSize: Int): PaginatedResponse<UserReportResponse> {
        val response = client.get("/api/v1/admin/user-reports") {
            parameter("page", page)
            parameter("pageSize", pageSize)
        }
        return response.body()
    }

    override suspend fun getImageQueue(page: Int, pageSize: Int, status: ImageStatus?): PaginatedResponse<AdminImageListItem> {
        val response = client.get("/api/v1/admin/images") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            status?.let { parameter("status", it.value) }
        }
        return response.body()
    }

    override suspend fun getModerationLogs(page: Int, pageSize: Int, adminId: String?, action: String?, sortBy: String?, sortAsc: Boolean, dateFrom: String?, dateTo: String?): PaginatedResponse<ModerationLogEntry> {
        val response = client.get("/api/v1/admin/moderation-logs") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            adminId?.let { parameter("adminId", it) }
            action?.let { parameter("action", it) }
            sortBy?.let { parameter("sortBy", it) }
            if (sortBy != null) parameter("sortAsc", sortAsc)
            dateFrom?.let { parameter("dateFrom", it) }
            dateTo?.let { parameter("dateTo", it) }
        }
        return response.body()
    }

    override suspend fun dismissReport(reportId: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/reports/$reportId/dismiss")
        if (!response.status.isSuccess()) error("Failed to dismiss report (${response.status.value})")
    }

    override suspend fun actionReport(reportId: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/reports/$reportId/action")
        if (!response.status.isSuccess()) error("Failed to action report (${response.status.value})")
    }

    override suspend fun dismissUserReport(reportId: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/user-reports/$reportId/dismiss")
        if (!response.status.isSuccess()) error("Failed to dismiss user report (${response.status.value})")
    }

    override suspend fun actionUserReport(reportId: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/user-reports/$reportId/action")
        if (!response.status.isSuccess()) error("Failed to action user report (${response.status.value})")
    }

    override suspend fun approveImage(id: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/images/$id/approve")
        if (!response.status.isSuccess()) error("Failed to approve image (${response.status.value})")
    }

    override suspend fun rejectImage(id: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/images/$id/reject")
        if (!response.status.isSuccess()) error("Failed to reject image (${response.status.value})")
    }

    override suspend fun suspendListing(listingId: String, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/listings/$listingId/suspend") {
            contentType(ContentType.Application.Json)
            setBody(ReasonRequest(reason))
        }
        if (!response.status.isSuccess()) error("Failed to suspend listing (${response.status.value})")
    }

    override suspend fun restoreListing(listingId: String, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/listings/$listingId/restore") {
            contentType(ContentType.Application.Json)
            setBody(ReasonRequest(reason))
        }
        if (!response.status.isSuccess()) error("Failed to restore listing (${response.status.value})")
    }

    override suspend fun warnUser(userId: String, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/users/$userId/warn") {
            contentType(ContentType.Application.Json)
            setBody(ReasonRequest(reason))
        }
        if (!response.status.isSuccess()) error("Failed to warn user (${response.status.value})")
    }

    override suspend fun suspendUser(userId: String, durationDays: Int, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/users/$userId/suspend") {
            contentType(ContentType.Application.Json)
            setBody(SuspendUserRequest(durationDays, reason))
        }
        if (!response.status.isSuccess()) error("Failed to suspend user (${response.status.value})")
    }

    override suspend fun banUser(userId: String, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/users/$userId/ban") {
            contentType(ContentType.Application.Json)
            setBody(ReasonRequest(reason))
        }
        if (!response.status.isSuccess()) error("Failed to ban user (${response.status.value})")
    }

    override suspend fun restoreUser(userId: String, reason: String?): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/users/$userId/restore") {
            contentType(ContentType.Application.Json)
            setBody(ReasonRequest(reason))
        }
        if (!response.status.isSuccess()) error("Failed to restore user (${response.status.value})")
    }

    override suspend fun getQueue(page: Int, pageSize: Int, source: String?, sort: String?, reason: String?, view: String?): AdminQueueResponse? {
        val response = client.get("/api/v1/admin/queue") {
            parameter("page", page)
            parameter("pageSize", pageSize)
            source?.let { parameter("source", it) }
            sort?.let { parameter("sort", it) }
            reason?.let { parameter("reason", it) }
            view?.let { parameter("view", it) }
        }
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminQueueResponse>>().data
    }

    override suspend fun bulkAction(request: BulkActionRequest): Result<BulkActionResponse> = runCatching {
        val response = client.post("/api/v1/admin/bulk-action") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) error("Bulk action failed (${response.status.value})")
        response.body<ApiResponse<BulkActionResponse>>().data ?: error("Empty response")
    }

    override suspend fun getSavedViews(): SavedViewsResponse? {
        val response = client.get("/api/v1/admin/me/saved-views")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<SavedViewsResponse>>().data
    }

    override suspend fun putSavedViews(views: List<SavedView>): Result<Unit> = runResult {
        val response = client.put("/api/v1/admin/me/saved-views") {
            contentType(ContentType.Application.Json)
            setBody(SavedViewsRequest(views = views))
        }
        if (!response.status.isSuccess()) error("Save failed (${response.status.value})")
    }

    override suspend fun listNotes(targetType: String, targetId: String): AdminNotesResponse? {
        val response = client.get("/api/v1/admin/notes/$targetType/$targetId")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminNotesResponse>>().data
    }

    override suspend fun createNote(targetType: String, targetId: String, body: String): Result<AdminNote> = runCatching {
        val response = client.post("/api/v1/admin/notes/$targetType/$targetId") {
            contentType(ContentType.Application.Json)
            setBody(CreateNoteRequest(body = body))
        }
        if (!response.status.isSuccess()) error("Create note failed (${response.status.value})")
        response.body<ApiResponse<AdminNote>>().data ?: error("Empty response")
    }

    override suspend fun deleteNote(noteId: String): Result<Unit> = runResult {
        val response = client.delete("/api/v1/admin/notes/$noteId")
        if (!response.status.isSuccess()) error("Delete note failed (${response.status.value})")
    }

    override suspend fun listAuditEntries(targetType: String, targetId: String): AuditEntriesResponse? {
        val response = client.get("/api/v1/admin/audit/$targetType/$targetId")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AuditEntriesResponse>>().data
    }

    override suspend fun recordView(targetType: String, targetId: String): Result<Unit> = runResult {
        val response = client.post("/api/v1/admin/audit/view") {
            contentType(ContentType.Application.Json)
            setBody(RecordViewRequest(AdminTargetType.valueOf(targetType.uppercase()), targetId))
        }
        if (!response.status.isSuccess()) error("Record view failed (${response.status.value})")
    }

    override suspend fun getUserConversations(userId: String): AdminConversationsResponse? {
        val response = client.get("/api/v1/admin/users/$userId/conversations")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminConversationsResponse>>().data
    }

    override suspend fun getListingConversations(listingId: String): AdminConversationsResponse? {
        val response = client.get("/api/v1/admin/listings/$listingId/conversations")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminConversationsResponse>>().data
    }

    override suspend fun getConversationMessages(conversationId: String): AdminMessagesResponse? {
        val response = client.get("/api/v1/admin/conversations/$conversationId/messages")
        if (!response.status.isSuccess()) return null
        return response.body<ApiResponse<AdminMessagesResponse>>().data
    }

    private inline fun runResult(block: () -> Unit): Result<Unit> =
        runCatching { block() }
}
